import type React from 'react';

import toast from 'components/partials/toast/toast';

import Icons from 'assets/icons';

export const calcMidpointOrder = (
  items: Array<{ sortOrder: number }>,
  newIndex: number
): number => {
  const leftOrder = newIndex === 0 ? 0 : items[newIndex - 1].sortOrder;
  const rightOrder =
    newIndex === items.length - 1
      ? leftOrder + 1000
      : items[newIndex + 1].sortOrder;

  return (leftOrder + rightOrder) / 2;
};

export const showReorderError = (message?: string): void => {
  toast.error({
    title: 'Error',
    message: message || 'Failed to reorder. Please try again.',
  });
};

export type ModalActionType =
  | 'rename-group'
  | 'remove-group'
  | 'rename-regime'
  | 'duplicate-regime'
  | 'remove-regime';

export interface ModalEntity {
  id?: string | number;
  groupId?: string | number;
  name?: string;
  enabled?: boolean;
  sortOrder?: number;
  [key: string]: unknown;
}

export interface ModalState {
  type: ModalActionType;
  entity: ModalEntity;
}

export interface SidebarAction {
  label: string;
  onClick: () => void;
  startIcon: React.JSX.Element;
  isDisabled?: boolean;
  isDelete?: boolean;
}

interface MutationLike {
  mutateAsync(data: unknown): Promise<unknown>;
}

interface ExecuteModalActionParams {
  type: ModalActionType | string;
  entity?: ModalEntity;
  arg?: string | number | null;
  // Method-call signature uses bivariant parameter checking, so concrete
  // TanStack `UseMutationResult.mutateAsync` (with its narrow `TVariables`)
  // is assignable to this loose contract under `strictFunctionTypes`.
  mutations: Record<string, MutationLike>;
  userId?: string;
  modelId?: string | number;
}

export const executeModalAction = async (
  params: ExecuteModalActionParams
): Promise<void> => {
  const { type, mutations, userId, modelId } = params;
  const entity = params.entity ?? {};
  const arg = params.arg;
  const {
    updateGroupMut,
    deleteGroupMut,
    updateRegimeMut,
    deleteRegimeMut,
    copyRegimeMut,
  } = mutations;

  switch (type) {
    case 'rename-group':
      await updateGroupMut.mutateAsync({
        userId,
        modelId,
        groupId: entity.id,
        data: {
          name: arg,
          enabled: entity.enabled,
          sortOrder: entity.sortOrder,
        },
      });
      toast.success({
        title: 'Group renamed',
        message: `Group has been renamed to "${arg}".`,
      });
      break;

    case 'remove-group':
      await deleteGroupMut.mutateAsync({
        userId,
        modelId,
        groupId: entity.id,
      });
      toast.success({
        title: 'Group removed',
        message: `"${entity.name}" has been removed.`,
      });
      break;

    case 'rename-regime':
      await updateRegimeMut.mutateAsync({
        userId,
        modelId,
        regimeId: entity.id,
        data: { name: arg },
      });
      toast.success({
        title: 'Regime renamed',
        message: `Regime has been renamed to "${arg}".`,
      });
      break;

    case 'duplicate-regime':
      await copyRegimeMut.mutateAsync({
        userId,
        modelId,
        regimeId: entity.id,
        data: { groupId: entity.groupId, name: arg },
      });
      toast.success({
        title: 'Regime duplicated',
        message: `"${arg}" has been created.`,
      });
      break;

    case 'remove-regime':
      await deleteRegimeMut.mutateAsync({
        userId,
        modelId,
        regimeId: entity.id,
      });
      toast.success({
        title: 'Regime removed',
        message: `"${entity.name}" has been removed.`,
      });
      break;

    default:
      break;
  }
};

export interface MoveActionOptions {
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isMoveUpDisabled?: boolean;
  isMoveDownDisabled?: boolean;
}

// Non-drag keyboard/click alternative to the drag-and-drop reorder (WCAG
// 2.5.7 Dragging Movements) — reuses the same reorder state-update functions
// the drag handlers call (handleGroupReorder / regime move), just triggered
// from a menu item instead of a pointer drag.
const buildMoveActions = (moveOptions?: MoveActionOptions): SidebarAction[] => {
  if (!moveOptions?.onMoveUp && !moveOptions?.onMoveDown) return [];

  return [
    {
      label: 'Move up',
      onClick: () => moveOptions.onMoveUp?.(),
      isDisabled: moveOptions.isMoveUpDisabled,
      startIcon: <Icons.ArrowUp customClass="size-4" />,
    },
    {
      label: 'Move down',
      onClick: () => moveOptions.onMoveDown?.(),
      isDisabled: moveOptions.isMoveDownDisabled,
      startIcon: <Icons.ArrowDown customClass="size-4" />,
    },
  ];
};

export const buildGroupActions = (
  group: ModalEntity,
  setModalState: (state: ModalState) => void
): SidebarAction[] => [
  {
    label: 'Rename',
    onClick: () => setModalState({ type: 'rename-group', entity: group }),
    startIcon: <Icons.Edit customClass="size-4" />,
  },
  {
    label: 'Remove',
    onClick: () => setModalState({ type: 'remove-group', entity: group }),
    isDelete: true,
    startIcon: <Icons.Delete customClass="size-4" />,
  },
];

export const buildRegimeActions = (
  regime: ModalEntity,
  setModalState: (state: ModalState) => void,
  moveOptions?: MoveActionOptions
): SidebarAction[] => [
  ...buildMoveActions(moveOptions),
  {
    label: 'Rename',
    onClick: () => setModalState({ type: 'rename-regime', entity: regime }),
    startIcon: <Icons.Edit customClass="size-4" />,
  },
  {
    label: 'Duplicate',
    onClick: () => setModalState({ type: 'duplicate-regime', entity: regime }),
    startIcon: <Icons.Copy customClass="size-4" />,
  },
  {
    label: 'Remove',
    onClick: () => setModalState({ type: 'remove-regime', entity: regime }),
    isDelete: true,
    startIcon: <Icons.Delete customClass="size-4" />,
  },
];
